/**
 * The Baya / Mehdi differences, as configuration (SPEC §5.1).
 *
 * One code base, one table. Note what the key is: **the level**, not the child.
 * The child shell never learns who the other child is, and a profile keyed by
 * name would be one more place holding that knowledge. A twelve-year-old gets
 * an airy screen because they are in 5ᵉ, not because they are Baya.
 */

export type Density = "airy" | "compact";
export type Tone = "encouraging" | "factual";

export type Profile = {
  /** Vertical rhythm and tap-target size. */
  density: Density;
  /** How the shell addresses the child. Never a judgement, in either case. */
  tone: Tone;
  /** One instruction per screen for the youngest (SPEC §11). */
  oneInstructionPerScreen: boolean;
  /** Editable weekly objectives — a seventeen-year-old sets his own. */
  weeklyGoals: boolean;
  /**
   * The whole year's competency map, seen / worked (§5.9).
   *
   * Three hundred lines, most of them saying « pas encore » : a map for
   * someone steering their own year, a wall for someone who is twelve. The
   * route answers for both children — this only decides whether the tab is
   * offered.
   */
  fullMap: boolean;
  /**
   * The bac countdown at the top of « Mon travail » (§5.7, « Échéances »).
   *
   * Terminale only: a première sits the French anticipated exam, which nobody
   * asked for, and a collégien has no bac to count down to. The server returns
   * no bac line outside terminale either — this decides whether the shell
   * would draw one.
   */
  bac: boolean;
  /**
   * The child's own figures (§14.1, relaxed on 29 Sept 2026 for the lycée only).
   *
   * Scores on 0–100, the overall position the parent reads, exercises done and
   * passed at first attempt, « N réussis sur M » at the end of a séance. A
   * seventeen-year-old steering his year reads them as a tool; a
   * twelve-year-old would read them as a mark — so the collège keeps the
   * invariant whole. The server applies the same rule
   * (`core_api/internal/niveau.py`) and sends no figure to a collégien: this
   * flag only decides whether the shell draws what it received.
   */
  ownNumbers: boolean;
  /**
   * The child composes his own training sheets (« Entraînement », 5 Oct 2026).
   *
   * Hedi : Mehdi picks exercises from the books or has some generated, works
   * them on paper, and closes the sheet when he wants to read the answers.
   * Steering one's own revision is a lycée gesture; a collégienne gets the
   * sheet her parent prepared (« Ce que ton parent t'a préparé ») and nothing
   * to compose. As with `fullMap`, the routes answer for both children — this
   * only decides whether the tab is offered.
   */
  ownTraining: boolean;
  /** Ceiling on interface sentence length, in words (SPEC §15). */
  maxSentenceWords: number;
  /** Which device this shell is drawn for. */
  device: "tablet" | "phone";
};

const COLLEGE: Profile = {
  density: "airy",
  tone: "encouraging",
  oneInstructionPerScreen: true,
  weeklyGoals: false,
  fullMap: false,
  bac: false,
  ownNumbers: false,
  ownTraining: false,
  maxSentenceWords: 12,
  device: "tablet",
};

const LYCEE: Profile = {
  density: "compact",
  tone: "factual",
  oneInstructionPerScreen: false,
  weeklyGoals: true,
  fullMap: true,
  bac: false,
  ownNumbers: true,
  ownTraining: true,
  maxSentenceWords: 22,
  device: "phone",
};

/** The lycée profile, plus the year that ends with the bac. */
const TERMINALE: Profile = { ...LYCEE, bac: true };

const BY_LEVEL: Record<string, Profile> = {
  "6e": COLLEGE,
  "5e": COLLEGE,
  "4e": COLLEGE,
  "3e": COLLEGE,
  "2de": LYCEE,
  "1re": LYCEE,
  terminale: TERMINALE,
  tle: TERMINALE,
};

const normalise = (level: string): string =>
  level
    .toLowerCase()
    .normalize("NFD")
    // Combining marks are not [a-z0-9]; the next line drops them with the rest.
    .replace(/[^a-z0-9]/g, "");

/** The airy profile is the default: it is the one that costs nothing to be wrong about. */
export function profileForLevel(level: string | null | undefined): Profile {
  if (!level) return COLLEGE;
  const key = normalise(level);
  const found = Object.entries(BY_LEVEL).find(([name]) => key.startsWith(normalise(name)));
  return found ? found[1] : COLLEGE;
}
